import React, { useState, useEffect } from "react";
import {
  X,
  Users,
  Folder,
  Search,
  AlertCircle,
  Check,
  File,
  FileText,
} from "lucide-react";

const InviteFolderModal = ({
  open,
  onClose,
  selectedFolder,
  members = [],
  currentUserId,
  onInvite,
  loading = false,
  invitedMembers = [],
  sharedFolders = [],
  sharedFiles = [],
}) => {
  const [selectedMembers, setSelectedMembers] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setSelectedMembers(new Set());
      setSearchQuery("");
      setError("");
    }
  }, [open]);

  if (!open || !selectedFolder) return null;

  // 초대 가능한 멤버 필터링 (is_me가 true인 사람 제외)
  const invitableMembers = members.filter((m) => !m.is_me);

  // 이미 초대된 멤버의 ID 목록
  const invitedMemberIds = new Set(invitedMembers.map((m) => m.id));

  // 초대되지 않은 멤버만 필터링
  const notInvitedMembers = invitableMembers.filter(
    (m) => !invitedMemberIds.has(m.id)
  );

  // 검색 필터링
  const filteredMembers = notInvitedMembers.filter((m) =>
    m.user_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleMemberToggle = (memberId) => {
    const newSelected = new Set(selectedMembers);
    if (newSelected.has(memberId)) {
      newSelected.delete(memberId);
    } else {
      newSelected.add(memberId);
    }
    setSelectedMembers(newSelected);
  };

  const handleInvite = async () => {
    if (selectedMembers.size === 0) {
      setError("초대할 멤버를 선택해주세요.");
      return;
    }

    // 각 멤버에 대해 초대 처리
    const invitePromises = Array.from(selectedMembers).map((memberId) =>
      onInvite(memberId, selectedFolder.id)
    );

    try {
      const results = await Promise.all(invitePromises);
      const failedInvites = results.filter((r) => !r).length;

      if (failedInvites > 0) {
        setError(`${failedInvites}명의 초대에 실패했습니다.`);
      } else {
        onClose();
      }
    } catch (err) {
      setError("초대 처리 중 오류가 발생했습니다.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5" />
            폴더 멤버 초대
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 본문 */}
        <div className="flex-1 overflow-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 text-red-800 text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {/* 폴더 정보 표시 */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="text-sm font-medium text-blue-900 mb-1">
              초대할 폴더
            </h3>
            <div className="flex items-center gap-3">
              <Folder className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium text-gray-900">
                  {selectedFolder.folder_name}
                </p>
                {selectedFolder.path && (
                  <p className="text-xs text-gray-600">{selectedFolder.path}</p>
                )}
              </div>
            </div>
          </div>

          {/* 공유될 콘텐츠 표시 */}
          {(sharedFolders.length > 0 || sharedFiles.length > 0) && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="text-sm font-medium text-blue-900 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />이 폴더를 공유하면 다음 콘텐츠도
                함께 공유됩니다
              </h3>

              {sharedFolders.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-blue-800 mb-2">
                    하위 폴더 ({sharedFolders.length}개)
                  </p>
                  <div className="space-y-1 max-h-20 overflow-y-auto">
                    {sharedFolders.map((folder) => (
                      <div
                        key={folder.id}
                        className="flex items-center gap-2 text-xs text-blue-700"
                      >
                        <Folder className="w-3 h-3" />
                        <span>{folder.folder_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {sharedFiles.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-blue-800 mb-2">
                    파일 ({sharedFiles.length}개)
                  </p>
                  <div className="space-y-1 max-h-20 overflow-y-auto">
                    {sharedFiles.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center gap-2 text-xs text-blue-700"
                      >
                        <File className="w-3 h-3" />
                        <span>{file.file_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 현재 초대된 멤버 목록 */}
          {invitedMembers.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                현재 공유된 멤버
              </h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {invitedMembers.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center p-3 rounded-lg bg-gray-50 border border-gray-200"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {member.user_name}
                        {member.is_me && (
                          <span className="ml-2 text-xs text-gray-500">
                            (나)
                          </span>
                        )}
                      </p>
                    </div>
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 멤버 선택 */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              초대할 멤버 선택
            </h3>

            {members.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>멤버 목록을 불러오는 중...</p>
              </div>
            ) : (
              <>
                {/* 검색 */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="이름으로 검색"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* 멤버 목록 */}
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {filteredMembers.length === 0 ? (
                    <p className="text-center py-4 text-gray-500">
                      초대 가능한 멤버가 없습니다.
                    </p>
                  ) : (
                    filteredMembers.map((member) => (
                      <label
                        key={member.id}
                        className="flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedMembers.has(member.id)}
                          onChange={() => handleMemberToggle(member.id)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <div className="ml-3 flex-1">
                          <p className="font-medium text-gray-900">
                            {member.user_name}
                          </p>
                        </div>
                      </label>
                    ))
                  )}
                </div>

                {selectedMembers.size > 0 && (
                  <p className="mt-3 text-sm text-gray-600">
                    {selectedMembers.size}명 선택됨
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* 푸터 */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            취소
          </button>
          <button
            onClick={handleInvite}
            disabled={selectedMembers.size === 0 || loading}
            className={`px-4 py-2 text-white rounded-md transition-colors ${
              selectedMembers.size === 0 || loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {loading ? "초대 중..." : "초대하기"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteFolderModal;
